import { useEffect, useState } from 'react'

/**
 * Viewport width at or below which the admin shell switches from a permanent
 * sidebar to an off-canvas drawer. 900px keeps the 240px sidebar plus a
 * usable content column; below it the fixed sidebar consumed most of the
 * screen with no way to dismiss it.
 */
export const NAV_BREAKPOINT_PX = 900

/**
 * True when the viewport is at or below `maxWidthPx`.
 *
 * Fail-safe: when `window.matchMedia` is unavailable (non-browser rendering,
 * or a jsdom environment without the polyfill) this returns false — the
 * desktop layout — rather than throwing. A shell that renders wide on a
 * narrow screen is a cosmetic problem; a shell that throws is a blank page.
 */
export const useIsNarrow = (maxWidthPx: number = NAV_BREAKPOINT_PX): boolean => {
  const query = `(max-width: ${maxWidthPx}px)`

  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false
    }
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }
    const mql = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches)

    setIsNarrow(mql.matches)

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    // Safari < 14 and older jsdom stubs only expose the legacy API.
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [query])

  return isNarrow
}
